import { IconButton, Scrollbar, Spinner } from "@carbon6/c6-frontend-design";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import type { DatasetDetailController } from "../../feature/datasetDetailController";
import { resolveDatasetErrorMessage } from "../../feature/datasetFormatters";
import type { DatasetSection } from "../../feature/datasetSections";
import { holdDatasetFactsEditor } from "../../feature/datasetFactsEditor";
import { sectionTabId } from "./DatasetSectionTabs";
import { DatasetSummaryCard } from "./DatasetSummaryCard";
import { DatasetFactsSection } from "./DatasetFactsSection";
import { holdInspectDataset } from "@/modules/inspect/public";
import { EpisodeInspectSection } from "./EpisodeInspectSection";
import { HideDatasetModal } from "./HideDatasetModal";
import { ProbeSection } from "./ProbeSection";
import { ProbeTableSource } from "../ProbeTableSource";
import { RequestProbeModal } from "./RequestProbeModal";
import { SourceFileSection } from "./SourceFileSection";
import styles from "./DatasetDetailBody.module.css";

type DatasetDetailBodyProps = {
  controller: DatasetDetailController;
  section: DatasetSection;
  /** 이력 표 · 점검 섹션이 다른 Probe 를 고를 때 — 소유자가 경로(route)나 창 상태를 옮긴다. */
  onOpenProbe: (probeUuid: string, section: DatasetSection) => void;
  /** 창이 전체화면으로 보이는지(route 는 항상 true). 점검 뷰어가 재생을 멈추는 축이다. */
  active: boolean;
  /** 섹션 탭 · 상세 pane 의 id 접두. 창과 route 가 한 화면에 함께 있어도 겹치지 않게 한다. */
  idPrefix: string;
  /** Dataset 을 못 찾았을 때 문구 아래 둘 행동(예: 목록으로). 창은 헤더의 닫기가 있어 비운다. */
  notFoundAction?: ReactNode;
  inspectHeaderHost?: HTMLElement | null;
};

/**
 * 상세 본문 — 고른 섹션의 pane 하나. 섹션 탭은 셸 헤더가 든다(창은 `stepHeader`, route 는 제목
 * 줄 아래). 창(`FullscreenWindow`)과 route(`PageBase`)가 같은 본문을 쓰고, 인셋은 셸이 소유하며
 * 이 루트는 padding · margin 을 두지 않는다(규칙 §4).
 *
 * 높이 · 스크롤은 루트가 `height: 100%` 로 채우고 pane 이 스크롤을 갖는다 — 셸은 `fill`
 * (창은 `bodyLayout="fill"`, route 는 `scrollable` 끔).
 */
export function DatasetDetailBody({
  active,
  controller,
  idPrefix,
  inspectHeaderHost,
  notFoundAction,
  onOpenProbe,
  section,
}: DatasetDetailBodyProps) {
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const { dataset, dialog, loadErrorMessage, loading, nowMs, probeErrorMessage, probes } = snapshot;
  const probe = controller.currentProbe();
  const panelId = `${idPrefix}-panel`;

  // 이력 표 소스는 본문(page 층)이 소유한다 — 컨트롤러는 배열만 들고 표 배선을 모른다. 스냅샷의
  // 이력이 바뀔 때마다(폴링 · 접수) 같은 배열을 흘려 표와 카드가 같은 값을 본다.
  const [probeSource] = useState(
    () =>
      new ProbeTableSource({
        onOpen: (probeUuid) => onOpenProbe(probeUuid, "probes"),
        resolveErrorMessage: resolveDatasetErrorMessage,
      }),
  );
  useEffect(() => {
    void probeSource.setProbes(probes);
  }, [probeSource, probes]);
  useEffect(() => () => probeSource.dispose(), [probeSource]);
  // 고른 episode 는 본문이 든다 — 점검 섹션은 다른 섹션에 다녀올 때마다 새로 마운트되는데,
  // 돌아와서도 보던 episode 를 봐야 한다.
  const [episode, setEpisode] = useState<string | null>(null);
  const probeUuid = probe?.uuid ?? null;
  useEffect(() => {
    // 다른 Probe 로 옮기면 그 건의 첫 episode 로 돌아간다.
    setEpisode(null);
  }, [probeUuid]);
  // 상세가 이 Dataset 의 점검 세션(받아 둔 Parquet) · 매핑 편집을 잡는다. 같은 Dataset 을 창과 route 페이지가
  // 함께 열 수 있어, 마지막으로 닫는 상세가 놓을 때만 버린다.
  useEffect(() => holdInspectDataset(controller.datasetUuid), [controller]);

  // 조사 사실 편집도 같은 방식으로 잡는다.
  useEffect(() => holdDatasetFactsEditor(controller.datasetUuid), [controller]);

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner label="Dataset 을 불러오는 중" size="md" />
      </div>
    );
  }

  if (dataset === null) {
    return (
      <div className={styles.center}>
        <p className={styles.notFound} role="alert">
          {loadErrorMessage ?? "요청한 Dataset 을 찾을 수 없습니다."}
        </p>
        {notFoundAction}
      </div>
    );
  }

  // 점검은 창 높이를 채우는 작업대다 — 재생바가 바닥에 있어야 해서 pane 이 스크롤을 갖지 않고
  // 점검 뷰어 안의 구역들이 각자 스크롤한다. 나머지 섹션은 그대로 문서처럼 흐른다.
  const fillBody = section === "inspect" || section === "files";

  return (
    <div className={styles.layout}>
      <div
        className={styles.pane}
        id={panelId}
        role="tabpanel"
        aria-labelledby={sectionTabId(idPrefix, section)}
      >
        {/* 머리글은 두지 않는다 — 헤더 탭이 이미 어느 섹션인지 말하고 있어 같은 라벨이 두 번 보인다. */}
        <Scrollbar
          className={styles.paneBody}
          gutter="auto"
          data-fill={fillBody ? "true" : "false"}
        >
          {section === "overview" ? (
            <DatasetSummaryCard dataset={dataset} revision={controller.currentRevision()} />
          ) : section === "files" ? (
            <SourceFileSection datasetName={dataset.displayName} datasetUuid={dataset.uuid} />
          ) : section === "probes" ? (
            <ProbeSection
              errorMessage={probeErrorMessage}
              nowMs={nowMs}
              probe={probe}
              source={probeSource}
              onRetry={controller.openRequestProbeDialog}
            />
          ) : section === "inspect" ? (
            <EpisodeInspectSection
              inspectHeaderHost={inspectHeaderHost}
              datasetUuid={dataset.uuid}
              active={active}
              probe={probe}
              probes={probes}
              selected={episode}
              onSelect={setEpisode}
              onOpenProbe={(probeUuid) => onOpenProbe(probeUuid, section)}
            />
          ) : (
            <DatasetFactsSection datasetUuid={dataset.uuid} active={active} />
          )}
        </Scrollbar>
      </div>
      {dialog.kind === "requestProbe" ? (
        <RequestProbeModal
          datasetName={dataset.displayName}
          episodeTarget={dialog.episodeTarget}
          errorMessage={dialog.errorMessage}
          isRetry={dialog.isRetry}
          submitting={dialog.submitting}
          onCancel={controller.closeDialog}
          onConfirm={controller.confirmRequestProbe}
          onEpisodeTargetChange={controller.setEpisodeTarget}
        />
      ) : null}
      {dialog.kind === "hide" ? (
        <HideDatasetModal
          datasetName={dataset.displayName}
          errorMessage={dialog.errorMessage}
          submitting={dialog.submitting}
          onCancel={controller.closeDialog}
          onConfirm={controller.confirmHide}
        />
      ) : null}
    </div>
  );
}

/** 목록으로 돌아가는 행동 — route 페이지가 `notFoundAction` 으로 넘긴다. */
export function BackToListAction({ onBackToList }: { onBackToList: () => void }) {
  return <IconButton text="목록으로" variant="surface" onClick={onBackToList} />;
}
